import { useState, useEffect, useCallback, useRef } from "react";
import type { MutableRefObject } from "react";
import {
  useParseFieldZones,
} from "@/resources/ab-test/ab-test.api";
import { productApi } from "@/resources/product";
import { toast } from "@/hooks/use-toast";
import type { ContentType, AxisKey, ParsedField, AxisFingerprint } from "@/types/ab-tests";
import type { AiValidationResult } from "@/types/ai-validation";
import type { ABTestType } from "@/types/ab-tests";

interface UseCreateTestLogicParams {
  step: 1 | 2;
  parsedProductId: string;
  selectedBrandId: number | null | undefined;
  selectedFields: ContentType[];
  variantATitle: string;
  variantABullets: Record<string, string>;
  variantBTitle: string;
  variantBBullets: Record<string, string>;
  derivedTestType: ABTestType;
  targetPhrase: string;
  fingerprintData: { fingerprint: AxisFingerprint; summary: string } | null;
  fingerprintTarget: Record<string, number>;
  parsedZonesA: Record<string, ParsedField | null>;
  setHypothesis: (v: string) => void;
  setHypothesisValidation: (v: AiValidationResult | null) => void;
  setParsedZones: (fn: (prev: Record<string, ParsedField | null>) => Record<string, ParsedField | null>) => void;
  setParsedZonesA: (fn: (prev: Record<string, ParsedField | null>) => Record<string, ParsedField | null>) => void;
  /**
   * AUT-430 (WEB-009): bumped only by the two Variant B applies that write the hypothesis
   * (keyword, strategy -- WEB-010). Captured at *schedule* time so an apply that lands inside
   * the 600ms window is caught too; a moved counter drops the automatic run whole -- no
   * request if it hasn't fired yet, no `setHypothesis`/`setHypothesisValidation`/toast if it has.
   */
  applyHypothesisSeqRef: MutableRefObject<number>;
}

export function useCreateTestLogic({
  step, parsedProductId, selectedBrandId, selectedFields,
  variantATitle, variantABullets, variantBTitle, variantBBullets,
  derivedTestType, targetPhrase, fingerprintData, fingerprintTarget,
  parsedZonesA, setHypothesis, setHypothesisValidation, setParsedZones, setParsedZonesA,
  applyHypothesisSeqRef,
}: UseCreateTestLogicParams) {
  const [parsingFieldsA, setParsingFieldsA] = useState<Set<string>>(new Set());
  const [isGeneratingHypothesis, setIsGeneratingHypothesis] = useState(false);

  const parseFieldZonesMutation = useParseFieldZones();
  const generateHypothesisMutation = productApi.useGenerateHeroAbTestHypothesis();

  const parseFieldZonesMutationRef = useRef(parseFieldZonesMutation);
  parseFieldZonesMutationRef.current = parseFieldZonesMutation;
  const generateHypothesisMutationRef = useRef(generateHypothesisMutation);
  generateHypothesisMutationRef.current = generateHypothesisMutation;

  const setHypothesisRef = useRef(setHypothesis);
  setHypothesisRef.current = setHypothesis;
  const setHypothesisValidationRef = useRef(setHypothesisValidation);
  setHypothesisValidationRef.current = setHypothesisValidation;
  const setParsedZonesRef = useRef(setParsedZones);
  setParsedZonesRef.current = setParsedZones;
  const setParsedZonesARef = useRef(setParsedZonesA);
  setParsedZonesARef.current = setParsedZonesA;

  const parsedZonesARef = useRef(parsedZonesA);
  parsedZonesARef.current = parsedZonesA;
  const parsingFieldsARef = useRef(new Set<string>());
  const variantABulletsRef = useRef(variantABullets);
  variantABulletsRef.current = variantABullets;
  const variantATitleRef = useRef(variantATitle);
  variantATitleRef.current = variantATitle;

  const hypothesisContextRef = useRef({
    selectedBrandId, parsedProductId, derivedTestType, targetPhrase,
    selectedFields, fingerprintData, fingerprintTarget,
  });
  hypothesisContextRef.current = {
    selectedBrandId, parsedProductId, derivedTestType, targetPhrase,
    selectedFields, fingerprintData, fingerprintTarget,
  };

  const parseZonesForVariantA = useCallback((field: ContentType, text: string) => {
    const id = Number(parsedProductId);
    if (!Number.isFinite(id) || id <= 0 || !selectedBrandId || !text.trim() || parsingFieldsARef.current.has(field)) return;
    parsingFieldsARef.current.add(field);
    setParsingFieldsA(prev => new Set(prev).add(field));
    void parseFieldZonesMutationRef.current.mutateAsync({
      productId: id, brandId: selectedBrandId,
      fieldLabel: field === "title" ? "Title" : `Bullet ${field.split("-")[1]}`, fieldText: text,
    })
      .then(res => { setParsedZonesARef.current(prev => ({ ...prev, [field]: { source: text, zones: res.zones } })); })
      .catch(() => {})
      .finally(() => {
        parsingFieldsARef.current.delete(field);
        setParsingFieldsA(prev => { const next = new Set(prev); next.delete(field); return next; });
      });
  }, [parsedProductId, selectedBrandId]);

  const parseZonesForField = useCallback((field: ContentType) => {
    const id = Number(parsedProductId);
    if (!Number.isFinite(id) || id <= 0 || !selectedBrandId) return;
    const text = field === "title" ? (variantBTitle || variantATitle) : (variantBBullets[field] || variantABullets[field] || "");
    if (!text.trim()) return;
    void parseFieldZonesMutationRef.current.mutateAsync({
      productId: id, brandId: selectedBrandId,
      fieldLabel: field === "title" ? "Title" : `Bullet ${field.split("-")[1]}`, fieldText: text,
    })
      .then(res => { setParsedZonesRef.current(prev => ({ ...prev, [field]: { source: text, zones: res.zones } })); })
      .catch(() => {});
  }, [parsedProductId, selectedBrandId, variantBTitle, variantATitle, variantBBullets, variantABullets]);

  useEffect(() => {
    if (step !== 2) return;
    const id = Number(parsedProductId);
    if (!Number.isFinite(id) || id <= 0 || !selectedBrandId) return;
    for (const field of selectedFields) {
      if (!parsedZonesARef.current[field]) {
        parseZonesForVariantA(field as ContentType, field === "title" ? variantATitleRef.current : (variantABulletsRef.current[field] || ""));
      }
    }
  }, [step, selectedFields, selectedBrandId, parsedProductId, parseZonesForVariantA]);

  const canGenerateHypothesis = !!(selectedBrandId && selectedFields.length > 0);

  useEffect(() => {
    if (step !== 2 || !canGenerateHypothesis) return;
    // Recorded at schedule time, not at fire: an apply (WEB-010) that increments the ref and
    // succeeds inside this 600ms window must be caught too, not only one that lands after.
    const seqAtSchedule = applyHypothesisSeqRef.current;
    const timer = setTimeout(() => {
      if (applyHypothesisSeqRef.current !== seqAtSchedule) return; // an apply arrived first -- no request
      const ctx = hypothesisContextRef.current;
      if (!ctx.selectedBrandId) return;
      const numId = Number(ctx.parsedProductId);
      if (!Number.isFinite(numId) || numId <= 0) return;
      setIsGeneratingHypothesis(true);
      let axisDeltas: Record<string, number> | undefined;
      if (ctx.fingerprintData) {
        axisDeltas = {};
        for (const axis of (["benefit", "audience", "use-case", "ingredient", "format"] as AxisKey[])) {
          const layer = ctx.fingerprintData.fingerprint[axis];
          axisDeltas[axis] = ctx.fingerprintTarget[axis] - Math.round(layer.locked + layer.descriptor);
        }
      }
      void generateHypothesisMutationRef.current.mutateAsync({
        productId: numId, brandId: ctx.selectedBrandId,
        testType: ctx.derivedTestType, intentFrom: undefined, intentTo: undefined,
        targetKeyword: ctx.targetPhrase || undefined, selectedFields: ctx.selectedFields, axisDeltas,
      })
        .then(result => {
          if (applyHypothesisSeqRef.current !== seqAtSchedule) return; // an apply won the race -- drop this result whole
          setHypothesisRef.current(result.hypothesis);
          if (result.validation) setHypothesisValidationRef.current(result.validation);
        })
        .catch(() => {
          if (applyHypothesisSeqRef.current !== seqAtSchedule) return;
          toast({ title: "Generation failed", description: "Could not generate hypothesis. Try again.", variant: "destructive" });
        })
        .finally(() => { setIsGeneratingHypothesis(false); });
    }, 600);
    return () => clearTimeout(timer);
  }, [step, canGenerateHypothesis, selectedFields]);

  return { parsingFieldsA, isGeneratingHypothesis, parseZonesForField };
}
